import { useMemo, useState } from "react";

import { listHindiVoices } from "../audio";
import { useAppAuth } from "../auth";
import { navigate } from "../router";
import { saveSettingsSynced } from "../state/mutations";
import type { AppState } from "../state/store";
import { loadState, saveState } from "../state/store";
import { DualLabel } from "../ui/DualLabel";
import { InkButton } from "../ui/InkButton";
import { PaperPanel } from "../ui/PaperPanel";
import { RubricNumeral } from "../ui/RubricNumeral";
import { Shirorekha } from "../ui/Shirorekha";

const LENGTH_OPTIONS: (30 | 45)[] = [30, 45];
const PAPER_EVERY_OPTIONS = [3, 4, 5];

function downloadJson(filename: string, data: unknown): void {
  const blob = new Blob([JSON.stringify(data, null, 2)], {
    type: "application/json",
  });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  anchor.click();
  URL.revokeObjectURL(url);
}

/**
 * Settings — session-length, paper cadence, voice, and rate knobs, plus
 * data export and (in clerk mode) sign-out. Every edit here goes through
 * `saveSettingsSynced` so the change is persisted locally AND queued for
 * the next outbox flush.
 */
export function Settings() {
  const { mode, signOut } = useAppAuth();
  const [state, setState] = useState<AppState>(() => loadState());
  const voices = useMemo(() => listHindiVoices(), []);

  function update(next: AppState["settings"]): void {
    const updated = saveSettingsSynced(state, next, Date.now());
    saveState(updated);
    setState(updated);
  }

  function handleExport(): void {
    downloadJson("hindi-progress.json", loadState());
  }

  return (
    <main className="paper-grain flex min-h-dvh flex-col items-center px-6 py-12">
      <section className="w-full max-w-md">
        <Shirorekha className="pt-4">
          <DualLabel
            en="Settings"
            hi="सेटिंग्स"
            className="text-xs text-ink-soft uppercase"
          />
          <h1 className="mt-4 font-display text-5xl leading-[0.95] text-ink">
            <DualLabel en="Practice settings" hi="अभ्यास सेटिंग्स" />
          </h1>
        </Shirorekha>

        <PaperPanel className="mt-8">
          <p className="font-latin text-sm text-ink-soft">
            <DualLabel variant="inline" en="Session length" hi="सत्र की अवधि" />
          </p>
          <div className="mt-3 flex gap-3">
            {LENGTH_OPTIONS.map((length) => (
              <button
                key={length}
                type="button"
                onClick={() => update({ ...state.settings, length })}
                className={`border px-5 py-2 font-latin text-base transition-colors duration-150 ${
                  state.settings.length === length
                    ? "border-ink bg-ink text-paper"
                    : "border-rule bg-transparent text-ink hover:border-ink"
                }`}
              >
                <span lang="en">{length} min</span>
                <RubricNumeral
                  value={length}
                  className={`ml-1.5 text-[0.7em] opacity-70 ${
                    state.settings.length === length ? "!text-paper" : ""
                  }`}
                />
              </button>
            ))}
          </div>
        </PaperPanel>

        <PaperPanel className="mt-6">
          <p className="font-latin text-sm text-ink-soft">
            <DualLabel
              variant="inline"
              en="Paper practice every"
              hi="कागज़ अभ्यास हर"
            />
          </p>
          <div className="mt-3 flex gap-3">
            {PAPER_EVERY_OPTIONS.map((paperEvery) => (
              <button
                key={paperEvery}
                type="button"
                onClick={() => update({ ...state.settings, paperEvery })}
                className={`border px-5 py-2 font-latin text-base transition-colors duration-150 ${
                  state.settings.paperEvery === paperEvery
                    ? "border-ink bg-ink text-paper"
                    : "border-rule bg-transparent text-ink hover:border-ink"
                }`}
              >
                <span lang="en">every {paperEvery} sessions</span>
                <RubricNumeral
                  value={paperEvery}
                  className={`ml-1.5 text-[0.7em] opacity-70 ${
                    state.settings.paperEvery === paperEvery
                      ? "!text-paper"
                      : ""
                  }`}
                />
              </button>
            ))}
          </div>
        </PaperPanel>

        <PaperPanel className="mt-6">
          <p className="font-latin text-sm text-ink-soft">
            <DualLabel variant="inline" en="Voice" hi="आवाज़" />
          </p>
          {voices.length === 0 ? (
            <p className="mt-3 font-latin text-sm text-ink-soft italic">
              <span lang="en">
                No Hindi voice found. Add a Hindi voice in Chrome or macOS
                settings, then reopen this page.
              </span>
            </p>
          ) : (
            <select
              value={state.settings.voiceName ?? ""}
              onChange={(e) =>
                update({
                  ...state.settings,
                  voiceName: e.target.value || null,
                })
              }
              className="mt-3 w-full border border-rule bg-transparent px-3 py-2 font-latin text-base text-ink"
            >
              <option value="" lang="en">
                Default
              </option>
              {voices.map((voice) => (
                <option key={voice.name} value={voice.name}>
                  {voice.name} ({voice.lang})
                </option>
              ))}
            </select>
          )}
        </PaperPanel>

        <PaperPanel className="mt-6">
          <p className="font-latin text-sm text-ink-soft">
            <DualLabel variant="inline" en="Speaking pace" hi="बोलने की गति" />
          </p>
          <div className="mt-3 flex items-center gap-3">
            <input
              type="range"
              min={0.7}
              max={1.1}
              step={0.05}
              value={state.settings.rate}
              onChange={(e) =>
                update({ ...state.settings, rate: Number(e.target.value) })
              }
              className="flex-1 accent-sindoor"
              aria-label="Speaking pace"
            />
            <span
              className="font-latin text-sm text-ink tabular-nums"
              lang="en"
            >
              {state.settings.rate.toFixed(2)}
            </span>
            <RubricNumeral
              value={state.settings.rate.toFixed(2)}
              className="text-[0.7em] opacity-70"
            />
          </div>
        </PaperPanel>

        <div className="mt-8 flex flex-col gap-3">
          <InkButton variant="ghost" onClick={handleExport}>
            <DualLabel variant="inline" en="Export data" hi="डेटा निर्यात" />
          </InkButton>
          {mode === "clerk" && (
            <InkButton variant="ghost" onClick={() => signOut()}>
              <DualLabel variant="inline" en="Sign out" hi="साइन आउट" />
            </InkButton>
          )}
        </div>

        <footer className="mt-12 border-t border-rule pt-4">
          <button
            type="button"
            onClick={() => navigate("/")}
            className="font-latin text-sm text-ink-soft transition-colors duration-150 hover:text-sindoor"
          >
            <DualLabel variant="inline" en="Home" hi="घर चलें" />
          </button>
        </footer>
      </section>
    </main>
  );
}
